export type ItemPedidoWhatsapp = {
  quantidade: string;
  unidade: string;
  descricao: string;
};

export type SecaoPedidoWhatsapp = {
  titulo: string;
  itens: ItemPedidoWhatsapp[];
};

export type PedidoWhatsapp = {
  fornecedor: string;
  previsaoChegada: string;
  observacoes: string[];
  secoes: SecaoPedidoWhatsapp[];
};

export type ListaPedidoWhatsapp = {
  fornecedor: string;
  previsaoChegada: string;
  itens: ItemPedidoWhatsapp[];
};

/** Prefixo que o WhatsApp Web/Desktop coloca ao copiar mensagens: "[14:59, 14/10/2026] Fulano: ". */
const PREFIXO_MENSAGEM = /^\[[^\]]*\d{1,2}:\d{2}[^\]]*\]\s*[^:]{1,60}:\s*/;
/** "*" e "-" só contam como marcador quando seguidos de espaço; "*MIRA*" é negrito, não item. */
const MARCADOR = /^(?:[•●▪◦·]|[*\-–—>](?=\s))\s*/;
const HORA_SOLTA = /^\d{1,2}:\d{2}\s*[✓✔]*$/;

const UNIDADES = new Set([
  "un", "und", "unid", "unidade", "unidades",
  "kg", "kgs", "g", "gr", "grs",
  "l", "lt", "lts", "litro", "litros", "ml",
  "cx", "cxs", "caixa", "caixas",
  "pct", "pcts", "pacote", "pacotes",
  "lata", "latas", "fardo", "fardos",
  "emb", "embalagem", "embalagens",
  "saco", "sacos", "dz", "duzia", "duzias",
  "bandeja", "bandejas", "balde", "baldes",
  "galao", "galoes", "frasco", "frascos",
  "pote", "potes", "garrafa", "garrafas",
  "pc", "pcs", "peca", "pecas", "rolo", "rolos",
]);

const DIAS_POR_PADRAO: [RegExp, string][] = [
  [/\bsegunda\b/, "Segunda-feira"],
  [/\bterca\b/, "Terça-feira"],
  [/\bquarta\b/, "Quarta-feira"],
  [/\bquinta\b/, "Quinta-feira"],
  [/\bsexta\b/, "Sexta-feira"],
  [/\bsabado\b/, "Sábado"],
  [/\bdomingo\b/, "Domingo"],
];

export function normalizarTextoPedido(valor: string): string {
  return valor
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

function capitalizarSeMaiusculo(valor: string): string {
  if (valor !== valor.toUpperCase() || !/\p{L}/u.test(valor)) return valor;
  return valor
    .toLowerCase()
    .replace(/(^|\s)(\p{L})/gu, (_, espaco: string, letra: string) => espaco + letra.toUpperCase());
}

function nomeFornecedor(titulo: string): string {
  const semDoisPontos = titulo.replace(/:\s*$/, "");
  const primeiraParte = semDoisPontos.split(/\s+-\s+|\s*[—–]\s*/)[0] ?? "";
  const nome = primeiraParte
    .replace(/\bpedidos?\b(\s+(da|de|do)\s+semana)?/giu, "")
    .replace(/^\s*(da|de|do)\s+/iu, "")
    .replace(/\s+(da|de|do)\s*$/iu, "")
    .trim();
  return capitalizarSeMaiusculo(nome);
}

function ehTituloSecao(linha: string): boolean {
  if (/:\s*$/.test(linha)) return true;
  return (
    linha.length <= 60 &&
    /\p{L}{3,}/u.test(linha) &&
    linha === linha.toUpperCase() &&
    !/[.!?]$/.test(linha)
  );
}

function ehTitulo(linha: string): boolean {
  return !/:\s*$/.test(linha) || /\bpedido/iu.test(linha);
}

export function interpretarItemPedido(texto: string): ItemPedidoWhatsapp {
  const linha = texto.trim();
  const numero = linha.match(/^(\d+(?:[.,]\d+)?)\s*(?:x\s+)?(.*)$/i);
  if (!numero) return { quantidade: "", unidade: "", descricao: linha };
  const quantidade = numero[1];
  const resto = numero[2].trim();
  const separado = resto.match(/^(.*?)(?:\s*[—–]\s*|\s+-\s+)(.+)$/);
  if (separado && separado[1].length <= 40) {
    return {
      quantidade,
      unidade: separado[1].trim(),
      descricao: separado[2].trim(),
    };
  }
  const [primeira = "", ...demais] = resto.split(/\s+/);
  const chaveUnidade = normalizarTextoPedido(primeira).replace(/\.$/, "");
  if (UNIDADES.has(chaveUnidade) && demais.length > 0) {
    return { quantidade, unidade: primeira, descricao: demais.join(" ") };
  }
  if (!resto) return { quantidade: "", unidade: "", descricao: linha };
  return { quantidade, unidade: "", descricao: resto };
}

export function diaSemanaNoTexto(texto: string): string {
  const normalizado = normalizarTextoPedido(texto);
  for (const [padrao, label] of DIAS_POR_PADRAO) {
    if (padrao.test(normalizado)) return label;
  }
  return "";
}

export function interpretarPedidoWhatsapp(texto: string): PedidoWhatsapp {
  let fornecedor = "";
  let tituloLido = false;
  const observacoes: string[] = [];
  const textosLivres: string[] = [];
  const secoes: SecaoPedidoWhatsapp[] = [];
  let atual: SecaoPedidoWhatsapp | null = null;

  for (const bruta of texto.split(/\r?\n/)) {
    let linha = bruta.trim().replace(PREFIXO_MENSAGEM, "");
    if (!linha || HORA_SOLTA.test(linha)) continue;
    const marcador = linha.match(MARCADOR);
    if (marcador) linha = linha.slice(marcador[0].length);
    linha = linha.replace(/[*_~]/g, "").trim();
    if (!linha) continue;

    if (marcador || /^\d/.test(linha)) {
      if (!atual) {
        atual = { titulo: "", itens: [] };
        secoes.push(atual);
      }
      atual.itens.push(interpretarItemPedido(linha));
      continue;
    }
    textosLivres.push(linha);
    if (!tituloLido && secoes.length === 0 && ehTitulo(linha)) {
      tituloLido = true;
      fornecedor = nomeFornecedor(linha);
      continue;
    }
    if (ehTituloSecao(linha)) {
      atual = { titulo: linha.replace(/:\s*$/, "").trim(), itens: [] };
      secoes.push(atual);
      continue;
    }
    observacoes.push(linha);
  }

  return {
    fornecedor,
    previsaoChegada: diaSemanaNoTexto(textosLivres.join("\n")),
    observacoes,
    secoes: secoes.filter((secao) => secao.itens.length > 0),
  };
}

/** Pré-seleciona a parte de Araçatuba; sem ela, todas as partes. */
export function secoesPadraoDestino(secoes: SecaoPedidoWhatsapp[]): number[] {
  const destino = secoes.flatMap((secao, indice) =>
    normalizarTextoPedido(secao.titulo).includes("aracatuba") ? [indice] : []
  );
  return destino.length > 0 ? destino : secoes.map((_, indice) => indice);
}

export function listasDoPedido(
  pedido: PedidoWhatsapp,
  indices: number[]
): ListaPedidoWhatsapp[] {
  const escolhidas = indices
    .filter((indice) => indice >= 0 && indice < pedido.secoes.length)
    .sort((a, b) => a - b)
    .map((indice) => pedido.secoes[indice]);
  return escolhidas.map((secao) => ({
    fornecedor:
      escolhidas.length > 1 && secao.titulo
        ? [pedido.fornecedor, secao.titulo].filter(Boolean).join(" · ")
        : pedido.fornecedor,
    previsaoChegada: pedido.previsaoChegada,
    itens: secao.itens.map((item) => ({ ...item })),
  }));
}
